import type { EnrichedFeature } from '@/hooks/useGeoLayer';

export interface DrawerField {
  key: string;
  label: string;
  value: string;
  href?: string;
}

type FieldKind = 'text' | 'link' | 'yesNo';

interface RegistryFieldSpec {
  keys: string[];
  label: string;
  kind?: FieldKind;
}

/** Curated Ministry of Tourism / ArcGIS fields — everything else is hidden from the drawer. */
const REGISTRY_FIELD_SPECS: RegistryFieldSpec[] = [
  { keys: ['קוד_זיהוי_המלון', 'קוד זיהוי'], label: 'קוד זיהוי' },
  { keys: ['שם_התאגיד', 'שם התאגיד'], label: 'תאגיד' },
  { keys: ['שם_הרחוב', 'שם הרחוב'], label: 'רחוב' },
  { keys: ['מספר_בניין', 'מספר בניין'], label: 'מספר בניין' },
  { keys: ['שיוך_מוניציפלי', 'שיוך מוניציפלי'], label: 'שיוך מוניציפלי' },
  { keys: ['קוד_היישוב', 'קוד היישוב'], label: 'קוד יישוב' },
  { keys: ['האם_מבנה_לשימור'], label: 'מבנה לשימור', kind: 'yesNo' },
  { keys: ['TOCHNIT'], label: 'תוכנית' },
  { keys: ['KVUZ TRG', 'KVUZ_TRG'], label: 'קבוצת ייעוד' },
  { keys: ['KVUZ YEUD', 'KVUZ_YEUD'], label: 'קוד ייעוד' },
  { keys: ['TARGUMYEUD'], label: 'ייעוד' },
  { keys: ['גוגל', 'Google', 'GOOGLE'], label: 'חיפוש בגוגל', kind: 'link' },
  { keys: ['תמונות', 'Images', 'IMAGES'], label: 'תמונות', kind: 'link' },
];

const HEADER_ATTR_KEYS = {
  settlement: ['שם_היישוב', 'שם היישוב', 'Muni Heb'],
  region: ['reg', 'נפה'],
  district: ['שם_המחוז', 'שם המחוז'],
} as const;

function trimString(val: unknown): string | null {
  if (val === null || val === undefined) return null;
  const text = String(val).trim();
  return text.length > 0 ? text : null;
}

function normalize(text: string): string {
  return text.toLowerCase().replace(/\s+/g, ' ').trim();
}

function firstAttributeValue(
  attributes: Record<string, unknown>,
  keys: readonly string[],
): string | null {
  for (const key of keys) {
    const value = trimString(attributes[key]);
    if (value) return value;
  }
  return null;
}

function formatYesNo(value: string): string {
  const v = normalize(value);
  if (v === 'yes' || v === 'כן') return 'כן';
  if (v === 'no' || v === 'לא') return 'לא';
  return value;
}

function isHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

function formatFieldValue(value: string, kind: FieldKind | undefined): Pick<DrawerField, 'value' | 'href'> {
  if (kind === 'link' && isHttpUrl(value)) {
    return { value: 'פתח קישור', href: value };
  }
  if (kind === 'yesNo') {
    return { value: formatYesNo(value) };
  }
  return { value };
}

function shouldSkipRegistryValue(
  spec: RegistryFieldSpec,
  value: string,
  feature: EnrichedFeature,
  headerValues: Set<string>,
): boolean {
  if (spec.kind === 'link') return false;

  const norm = normalize(value);

  if (spec.keys.some((k) => k.includes('תאגיד')) && normalize(feature.name) === norm) {
    return true;
  }

  if (headerValues.has(norm)) return true;

  return false;
}

export function getDrawerHeaderMeta(feature: EnrichedFeature): string {
  const attrs = feature.attributes;
  const parts: string[] = [feature.layerDisplayName];

  const settlement = firstAttributeValue(attrs, HEADER_ATTR_KEYS.settlement);
  const region = firstAttributeValue(attrs, HEADER_ATTR_KEYS.region);
  const district = firstAttributeValue(attrs, HEADER_ATTR_KEYS.district);

  if (settlement) parts.push(settlement);
  if (region && normalize(region) !== normalize(settlement ?? '')) parts.push(region);
  if (district && !parts.some((p) => normalize(p) === normalize(district))) {
    parts.push(district);
  }

  return parts.join(' · ');
}

export function getDrawerRegistryFields(feature: EnrichedFeature): DrawerField[] {
  const headerValues = new Set(
    [
      feature.layerDisplayName,
      feature.name,
      ...HEADER_ATTR_KEYS.settlement.map((k) => trimString(feature.attributes[k])),
      ...HEADER_ATTR_KEYS.region.map((k) => trimString(feature.attributes[k])),
      ...HEADER_ATTR_KEYS.district.map((k) => trimString(feature.attributes[k])),
    ]
      .filter((v): v is string => Boolean(v))
      .map(normalize),
  );

  const fields: DrawerField[] = [];

  for (const spec of REGISTRY_FIELD_SPECS) {
    const raw = firstAttributeValue(feature.attributes, spec.keys);
    if (!raw || raw === 'mock') continue;
    if (shouldSkipRegistryValue(spec, raw, feature, headerValues)) continue;

    const { value, href } = formatFieldValue(raw, spec.kind);
    fields.push({
      key: spec.keys[0],
      label: spec.label,
      value,
      href,
    });
  }

  return fields;
}
